import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDateTimeAMPM } from "@/lib/dataTransformers";
import { formatDuration } from "@/lib/reportHelpers";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

interface DashboardReportStats {
  totalDischarges: number;
  initiatedDischarges: number;
  avgDischargeTime: string;
  pendingDischarges: number;
  paymentPending: number;
}

interface ExtendedReportStats {
  patientsMarked: number;
  plannedDischarge: number;
  losCarryOver: number;
  escalationsL1: number;
  escalationsL2: number;
  escalationsL3: number;
  admittedInPeriod: number;
}

interface StepTATData {
  step: string;
  name: string;
  avgMinutes: number;
  withinTAT: number;
  level1Breach: number;
  level2Breach: number;
  level3Breach: number;
  totalCompleted: number;
}

interface TATConfig {
  allowed: number;
  grace: number;
  level1: number;
  level2: number;
}

// Default TAT thresholds (fallback if not in database)
const DEFAULT_TAT_THRESHOLDS: Record<string, TATConfig> = {
  DSS: { allowed: 45, grace: 15, level1: 80, level2: 100 },
  DSF: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  CS: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  PC_PHARMACY: { allowed: 45, grace: 15, level1: 80, level2: 100 },
  BS: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  BA: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  PC: { allowed: 60, grace: 15, level1: 80, level2: 100 },
  PD: { allowed: 30, grace: 15, level1: 80, level2: 100 },
};

interface WardTATData {
  ward: string;
  avgMinutes: number;
  withinTAT: number;
  level1Breach: number;
  level2Breach: number;
  level3Breach: number;
  totalCompleted: number;
}

export interface PatientReportRow {
  patientName: string;
  apexId: string;
  dischargeInitiatedAt: string;
  totalElapsedTime: string;
  physicalDischargeAt: string;
  dischargeStatus: string;
  paymentStatus: string;
}

export interface ReportDashboardData {
  stats: DashboardReportStats;
  extended: ExtendedReportStats;
  stepwiseTAT: StepTATData[];
  wardwiseTAT: WardTATData[];
  patientRows: PatientReportRow[];
}

// Workflow steps configuration
const WORKFLOW_STEPS = [
  { step: "DSS", name: "Discharge Summary Saved" },
  { step: "DSF", name: "Discharge Summary Approved" },
  { step: "CS", name: "Charge Sheet" },
  { step: "PC_PHARMACY", name: "Pharmacy Clearance" },
  { step: "BS", name: "Bill Saved" },
  { step: "BA", name: "Bill Approved" },
  { step: "PC", name: "Payment Complete" },
  { step: "PD", name: "Physical Discharge" },
];

// Ward TAT thresholds in minutes (2 hours good, 4 hours warning)
const WARD_TAT_THRESHOLDS = { allowed: 120, grace: 60, level1: 80, level2: 100 };

export const useReportDashboardStats = (startDateTime: Date | null) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  const isValidDate = startDateTime && !isNaN(startDateTime.getTime());
  return useQuery<ReportDashboardData | null>({
    queryKey: ["report-dashboard-stats", organizationId, currentBranchId, isValidDate ? startDateTime.toISOString() : null] as const,
    queryFn: async (): Promise<ReportDashboardData | null> => {
      if (!startDateTime || isNaN(startDateTime.getTime())) return null;
      if (currentBranchId == null || !organizationId) return null;
      const orgId = organizationId;

      const endDateTime = new Date(startDateTime);
      endDateTime.setHours(endDateTime.getHours() + 24);

      const startISO = startDateTime.toISOString();
      const endISO = endDateTime.toISOString();
      const endBoundary = new Date(Math.min(endDateTime.getTime(), Date.now()));

      // Fetch completed PD activities for discharge count
      // @ts-ignore - Supabase PostgrestFilterBuilder causes excessively deep type instantiation
      const { data: pdActivities, error: pdError } = await supabase
        .from("activities")
        .select("id, patient_id, completion_time, started_at, time_taken_minutes")
        .eq("workflow_step", "PD")
        .eq("status", "completed")
        .eq("branch_id", currentBranchId)
        .eq("organization_id", orgId)
        .gte("completion_time", startISO)
        .lt("completion_time", endISO);

      if (pdError) throw pdError;

      // Fetch MD activities for discharge time calculation
      const { data: mdActivities, error: mdError } = await supabase
        .from("activities")
        .select("patient_id, started_at")
        .eq("workflow_step", "MD")
        .eq("status", "completed")
        .eq("branch_id", currentBranchId)
        .eq("organization_id", orgId);

      if (mdError) throw mdError;

      // Calculate average discharge time (MD start to PD completion)
      const mdMap = new Map(mdActivities?.map(a => [a.patient_id, a.started_at]) || []);
      let totalDischargeMinutes = 0;
      let dischargeCount = 0;

      pdActivities?.forEach(pd => {
        const mdStart = mdMap.get(pd.patient_id);
        if (mdStart && pd.completion_time) {
          const minutes = (new Date(pd.completion_time).getTime() - new Date(mdStart).getTime()) / (1000 * 60);
          if (minutes > 0) {
            totalDischargeMinutes += minutes;
            dischargeCount++;
          }
        }
      });

      const avgMinutes = dischargeCount > 0 ? totalDischargeMinutes / dischargeCount : 0;
      const hours = Math.floor(avgMinutes / 60);
      const mins = Math.floor(avgMinutes % 60);
      const avgDischargeTime = `${hours}h ${mins}m`;

      // Fetch pending discharges (initiated but not discharged within the window)
      const pendingPromise = (supabase
        .from("patients")
        .select("id")
        .eq("initiated", true)
        .neq("discharge_status", "discharged")
        .eq("branch_id", currentBranchId)
        .gte("created_at", startISO)
        .lt("created_at", endISO)) as unknown as Promise<{ data: { id: string }[] | null; error: Error | null }>;
      const { data: pendingPatients, error: pendingError } = await pendingPromise;

      if (pendingError) throw pendingError;

      // Fetch payment pending
      const { data: paymentPendingData, error: paymentError } = await supabase
        .from("activities")
        .select("id")
        .eq("workflow_step", "PC")
        .in("status", ["pending", "in-progress"])
        .eq("branch_id", currentBranchId)
        .gte("created_at", startISO)
        .lt("created_at", endISO);

      if (paymentError) throw paymentError;

      // Fetch discharge initiation (MD completed) in the selected window
      const { data: mdCompleted, error: mdCompletedError } = await supabase
        .from("activities")
        .select("patient_id, completion_time")
        .eq("workflow_step", "MD")
        .eq("status", "completed")
        .eq("branch_id", currentBranchId)
        .gte("completion_time", startISO)
        .lt("completion_time", endISO);

      if (mdCompletedError) throw mdCompletedError;

      const mdCompletionMap = new Map<string, string>();
      (mdCompleted || []).forEach((md) => {
        if (!md.patient_id || !md.completion_time) return;
        const existing = mdCompletionMap.get(md.patient_id);
        if (!existing || new Date(md.completion_time).getTime() > new Date(existing).getTime()) {
          mdCompletionMap.set(md.patient_id, md.completion_time);
        }
      });

      const reportPatientIdSet = new Set<string>(Array.from(mdCompletionMap.keys()));
      (pdActivities || []).forEach((pd) => {
        if (pd.patient_id) {
          reportPatientIdSet.add(pd.patient_id);
        }
      });
      const reportPatientIds = Array.from(reportPatientIdSet);

      let reportPatients: any[] = [];
      let pcActivities: any[] = [];
      let mdCompletionAllMap = new Map<string, string>();

      if (reportPatientIds.length > 0) {
        // Fetch MD completion times for all report patients (including earlier initiations)
        const { data: mdAllCompleted, error: mdAllError } = await supabase
          .from("activities")
          .select("patient_id, completion_time")
          .eq("workflow_step", "MD")
          .eq("status", "completed")
          .eq("branch_id", currentBranchId)
          .in("patient_id", reportPatientIds);

        if (mdAllError) throw mdAllError;
        (mdAllCompleted || []).forEach((md) => {
          if (!md.patient_id || !md.completion_time) return;
          const existing = mdCompletionAllMap.get(md.patient_id);
          if (!existing || new Date(md.completion_time).getTime() > new Date(existing).getTime()) {
            mdCompletionAllMap.set(md.patient_id, md.completion_time);
          }
        });

        // Fetch patient details for report rows
        const { data: patientsData, error: reportPatientsError } = await supabase
          .from("patients")
          .select("id, name, patient_id, discharge_status, physical_discharge_completion_time")
          .eq("branch_id", currentBranchId)
          .in("id", reportPatientIds);

        if (reportPatientsError) throw reportPatientsError;
        reportPatients = patientsData || [];

        // Fetch payment status (PC activity) for report patients
        const { data: pcData, error: pcError } = await supabase
          .from("activities")
          .select("patient_id, status, completion_time, created_at")
          .eq("workflow_step", "PC")
          .eq("branch_id", currentBranchId)
          .in("patient_id", reportPatientIds);

        if (pcError) throw pcError;
        pcActivities = pcData || [];
      }

      const paymentStatusMap = new Map<string, { status: string; time: number }>();
      pcActivities.forEach((pc: any) => {
        if (!pc.patient_id) return;
        const candidateTime = new Date(pc.completion_time || pc.created_at || 0).getTime();
        const existing = paymentStatusMap.get(pc.patient_id);
        if (!existing || candidateTime >= existing.time) {
          paymentStatusMap.set(pc.patient_id, { status: pc.status, time: candidateTime });
        }
      });

      const normalizePaymentStatus = (status?: string) => {
        if (!status) return "Auto Skipped";
        if (status === "completed") return "done";
        if (status === "pending" || status === "in-progress") return "pending";
        return "pending";
      };

      const patientRows: PatientReportRow[] = (reportPatients || []).map((patient) => {
        const mdCompletion = mdCompletionMap.get(patient.id) || mdCompletionAllMap.get(patient.id);
        const dischargeInitiatedAt = formatDateTimeAMPM(mdCompletion || null);
        const physicalDischargeAt = formatDateTimeAMPM(patient.physical_discharge_completion_time);
        const elapsedMinutes = mdCompletion
          ? Math.max(
              0,
              (new Date(patient.physical_discharge_completion_time || endBoundary).getTime() -
                new Date(mdCompletion).getTime()) / (1000 * 60),
            )
          : 0;
        const totalElapsedTime = mdCompletion ? formatDuration(elapsedMinutes) : "-";
        const paymentStatusRaw = paymentStatusMap.get(patient.id)?.status;

        return {
          patientName: patient.name || "",
          apexId: patient.patient_id || "",
          dischargeInitiatedAt: dischargeInitiatedAt || "",
          totalElapsedTime,
          physicalDischargeAt: physicalDischargeAt || "",
          dischargeStatus: patient.discharge_status === "discharged" ? "done" : "pending",
          paymentStatus: normalizePaymentStatus(paymentStatusRaw),
        };
      })
      .sort((a, b) => {
        const aTime = a.physicalDischargeAt ? new Date(a.physicalDischargeAt).getTime() : 0;
        const bTime = b.physicalDischargeAt ? new Date(b.physicalDischargeAt).getTime() : 0;
        if (aTime !== bTime) return bTime - aTime;
        const aInit = a.dischargeInitiatedAt ? new Date(a.dischargeInitiatedAt).getTime() : 0;
        const bInit = b.dischargeInitiatedAt ? new Date(b.dischargeInitiatedAt).getTime() : 0;
        return bInit - aInit;
      });

      // Fetch all completed activities for stepwise TAT
      type ActivityRow = { workflow_step: string | null; time_taken_minutes: number | null; started_at: string | null; completion_time: string | null };
      const actPromise = (supabase
        .from("activities")
        .select("workflow_step, time_taken_minutes, started_at, completion_time")
        .eq("status", "completed")
        .not("workflow_step", "is", null)
        .eq("branch_id", currentBranchId)
        .gte("completion_time", startISO)
        .lt("completion_time", endISO)) as unknown as Promise<{ data: ActivityRow[] | null; error: Error | null }>;
      const { data: allActivities, error: actError } = await actPromise;

      if (actError) throw actError;

      // Fetch TAT configurations from database
      const { data: tatConfigs } = await supabase
        .from("tat_configurations")
        .select("workflow_step, allowed_tat_minutes, level_1_threshold_percentage, level_2_threshold_percentage, grace_period_minutes")
        .eq("is_active", true);

      // Build config map from database
      const configMap: Record<string, TATConfig> = {};
      (tatConfigs || []).forEach((config) => {
        if (config.workflow_step) {
          configMap[config.workflow_step] = {
            allowed: config.allowed_tat_minutes,
            grace: config.grace_period_minutes || 15,
            level1: config.level_1_threshold_percentage || 80,
            level2: config.level_2_threshold_percentage || 100,
          };
        }
      });

      // Process stepwise TAT
      const stepwiseTAT: StepTATData[] = WORKFLOW_STEPS.map(({ step, name }) => {
        const stepActivities = (allActivities || []).filter(a => a.workflow_step === step);

        if (stepActivities.length === 0) {
          return { step, name, avgMinutes: 0, withinTAT: 0, level1Breach: 0, level2Breach: 0, level3Breach: 0, totalCompleted: 0 };
        }

        const timeTakenList = stepActivities.map(a => {
          if (a.time_taken_minutes) return a.time_taken_minutes;
          if (a.started_at && a.completion_time) {
            return (new Date(a.completion_time).getTime() - new Date(a.started_at).getTime()) / (1000 * 60);
          }
          return 0;
        }).filter(t => t > 0);

        if (timeTakenList.length === 0) {
          return { step, name, avgMinutes: 0, withinTAT: 0, level1Breach: 0, level2Breach: 0, level3Breach: 0, totalCompleted: 0 };
        }

        const stepAvg = timeTakenList.reduce((sum, t) => sum + t, 0) / timeTakenList.length;
        const thresholds = configMap[step] || DEFAULT_TAT_THRESHOLDS[step] || { allowed: 30, grace: 15, level1: 80, level2: 100 };

        // Calculate thresholds in minutes
        const level1ThresholdMinutes = thresholds.allowed * (thresholds.level1 / 100); // 80% of allowed
        const level2ThresholdMinutes = thresholds.allowed; // 100% of allowed
        const level3ThresholdMinutes = thresholds.allowed + thresholds.grace; // allowed + grace

        let withinTAT = 0, level1Breach = 0, level2Breach = 0, level3Breach = 0;
        timeTakenList.forEach(time => {
          if (time <= level1ThresholdMinutes) withinTAT++;
          else if (time <= level2ThresholdMinutes) level1Breach++;
          else if (time <= level3ThresholdMinutes) level2Breach++;
          else level3Breach++;
        });

        const total = timeTakenList.length;
        return {
          step,
          name,
          avgMinutes: stepAvg,
          withinTAT: Math.round((withinTAT / total) * 100),
          level1Breach: Math.round((level1Breach / total) * 100),
          level2Breach: Math.round((level2Breach / total) * 100),
          level3Breach: Math.round((level3Breach / total) * 100),
          totalCompleted: total,
        };
      });

      // Fetch patients with ward/room info for ward-wise TAT
      const { data: patientsWithWard, error: wardError } = await supabase
        .from("patients")
        .select("id, ward_name, room")
        .not("ward_name", "is", null)
        .eq("branch_id", currentBranchId);

      if (wardError) throw wardError;

      // Prefer human-readable room name when available; fallback to ward_name
      const patientWardMap = new Map(
        (patientsWithWard || []).map((p: any) => [
          p.id,
          p.room || p.ward_name || "Unknown",
        ])
      );

      // Calculate ward-wise TAT
      const wardTimesMap: Record<string, number[]> = {};

      pdActivities?.forEach(pd => {
        const ward = patientWardMap.get(pd.patient_id);
        const mdStart = mdMap.get(pd.patient_id);

        if (ward && mdStart && pd.completion_time) {
          const dischargeTime = (new Date(pd.completion_time).getTime() - new Date(mdStart).getTime()) / (1000 * 60);
          if (dischargeTime > 0) {
            if (!wardTimesMap[ward]) wardTimesMap[ward] = [];
            wardTimesMap[ward].push(dischargeTime);
          }
        }
      });

      const wardwiseTAT: WardTATData[] = Object.entries(wardTimesMap)
        .map(([ward, times]) => {
          const wardAvg = times.reduce((sum, t) => sum + t, 0) / times.length;

          // Calculate thresholds in minutes for wards
          const level1ThresholdMinutes = WARD_TAT_THRESHOLDS.allowed * (WARD_TAT_THRESHOLDS.level1 / 100); // 80%
          const level2ThresholdMinutes = WARD_TAT_THRESHOLDS.allowed; // 100%
          const level3ThresholdMinutes = WARD_TAT_THRESHOLDS.allowed + WARD_TAT_THRESHOLDS.grace; // allowed + grace

          let withinTAT = 0, level1Breach = 0, level2Breach = 0, level3Breach = 0;
          times.forEach(time => {
            if (time <= level1ThresholdMinutes) withinTAT++;
            else if (time <= level2ThresholdMinutes) level1Breach++;
            else if (time <= level3ThresholdMinutes) level2Breach++;
            else level3Breach++;
          });

          return {
            ward,
            avgMinutes: wardAvg,
            withinTAT: Math.round((withinTAT / times.length) * 100),
            level1Breach: Math.round((level1Breach / times.length) * 100),
            level2Breach: Math.round((level2Breach / times.length) * 100),
            level3Breach: Math.round((level3Breach / times.length) * 100),
            totalCompleted: times.length,
          };
        })
        .sort((a, b) => a.ward.localeCompare(b.ward));

      // === Extended Stats (matching dashboard) ===

      // Planned discharge (patients with expected_discharge within the 24hr window)
      // @ts-ignore - Supabase PostgrestFilterBuilder causes excessively deep type instantiation
      const { data: planned } = await supabase
        .from("patients")
        .select("id")
        .eq("branch_id", currentBranchId)
        .gte("expected_discharge", startISO)
        .lt("expected_discharge", endISO);

      // LOS carry-over: patients whose expected_discharge was before window start but not yet discharged
      const { data: carryOver } = await supabase
        .from("patients")
        .select("id")
        .eq("branch_id", currentBranchId)
        .lt("expected_discharge", startISO)
        .eq("initiated", true)
        .neq("discharge_status", "discharged");

      // Escalation counts from tat_alert_logs in the 24hr window
      // @ts-ignore - Supabase PostgrestFilterBuilder causes excessively deep type instantiation
      const { data: alerts } = await supabase
        .from("tat_alert_logs")
        .select("escalation_level")
        .eq("branch_id", currentBranchId)
        .gte("created_at", startISO)
        .lt("created_at", endISO);

      const escalationsL1 = alerts?.filter(a => a.escalation_level === 1).length || 0;
      const escalationsL2 = alerts?.filter(a => a.escalation_level === 2).length || 0;
      const escalationsL3 = alerts?.filter(a => a.escalation_level === 3).length || 0;

      // Patients admitted in the 24hr window
      const { data: admittedInPeriod } = await supabase
        .from("patients")
        .select("id")
        .eq("branch_id", currentBranchId)
        .gte("admission_datetime", startISO)
        .lt("admission_datetime", endISO);

      return {
        stats: {
          totalDischarges: pdActivities?.length || 0,
          initiatedDischarges: mdCompletionMap.size,
          avgDischargeTime,
          pendingDischarges: pendingPatients?.length || 0,
          paymentPending: paymentPendingData?.length || 0,
        },
        extended: {
          patientsMarked: mdCompletionMap.size,
          plannedDischarge: planned?.length || 0,
          losCarryOver: carryOver?.length || 0,
          escalationsL1,
          escalationsL2,
          escalationsL3,
          admittedInPeriod: admittedInPeriod?.length || 0,
        },
        stepwiseTAT,
        wardwiseTAT,
        patientRows,
      };
    },
    enabled: !!startDateTime && currentBranchId != null,
    refetchInterval: false,
  });
};
